import { browserZipLimit, MAX_BROWSER_ZIP_BYTES } from '../../utils/boundedZip';
import { isAwaitingMultiPackageOutput } from './multiPackageStatus';
import type {
  ChildPackageCandidate,
  MultiPackagePrintResultItem,
  SelectedChildPackage,
} from '../../types/multiPackagePrint';

export function matchesChildPackageSearch(packageLabel: string, query: string): boolean {
  const normalizedLabel = packageLabel.trim().toLowerCase();
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  if (normalizedQuery.length >= 4 && normalizedQuery.length <= 5) {
    return normalizedLabel.endsWith(normalizedQuery);
  }
  return normalizedLabel === normalizedQuery;
}

export function selectChildPackages(
  candidates: ChildPackageCandidate[],
  selectedMetrcPackageIds: number[],
  previousSelection: SelectedChildPackage[] = [],
): SelectedChildPackage[] {
  const selectedIds = new Set(selectedMetrcPackageIds);
  const includedIds = new Set<number>();
  const previousById = new Map(previousSelection.map((child) => [child.metrcPackageId, child]));
  return candidates
    .filter((candidate) => {
      if (!candidate.isEligible || !candidate.isImported || !selectedIds.has(candidate.metrcPackageId)
        || includedIds.has(candidate.metrcPackageId)) return false;
      includedIds.add(candidate.metrcPackageId);
      return true;
    })
    .map((candidate, sequence) => {
      const previous = previousById.get(candidate.metrcPackageId);
      return {
        ...candidate,
        sequence,
        count: previous ? previous.count : recommendedLabelCount(candidate.quantity),
        productOverrideId: previous?.productOverrideId ?? null,
      };
    });
}

export function reconcileSelectedChildPackages(
  candidates: ChildPackageCandidate[],
  selectedChildren: SelectedChildPackage[],
): SelectedChildPackage[] {
  const selectedIds = selectedChildren.map((child) => child.metrcPackageId);
  return selectChildPackages(candidates, selectedIds, selectedChildren);
}

export function recommendedLabelCount(quantity: number): number | null {
  if (!Number.isSafeInteger(quantity) || quantity <= 0) return null;
  // Integer arithmetic avoids fractional floating point rounding near whole-number boundaries.
  const recommendation = quantity + Math.ceil(quantity / 10);
  return Number.isSafeInteger(recommendation) ? recommendation : null;
}

export type CasePlan = {
  fullCases: number;
  remainingUnits: number;
  hasPartialCase: boolean;
};

export function calculateCasePlan(quantity: number, unitsPerCase: number): CasePlan | null {
  if (!Number.isSafeInteger(quantity) || quantity < 0 || !Number.isSafeInteger(unitsPerCase) || unitsPerCase <= 0) return null;
  const fullCases = Math.floor(quantity / unitsPerCase);
  const remainingUnits = quantity - fullCases * unitsPerCase;
  return { fullCases, remainingUnits, hasPartialCase: remainingUnits > 0 };
}

export function childCaseSize(child: ChildPackageCandidate): number | null {
  return ['countbased', 'count'].includes(child.unitOfMeasureQuantityType?.toLowerCase() ?? '')
    && Number.isSafeInteger(child.quantity) && child.quantity > 0
    ? child.quantity : null;
}

export function commonCasePlan(sourceQuantity: number, children: ChildPackageCandidate[]): CasePlan | null {
  // A source-wide plan must account for every immediate child, not only the
  // subset selected for printing. Unknown or variable sizes cannot establish
  // how many full cases the source represents.
  const caseSize = children[0] ? childCaseSize(children[0]) : null;
  if (caseSize === null || children.some((child) => childCaseSize(child) !== caseSize)
    || children.reduce((sum, child) => sum + child.quantity, 0) > sourceQuantity) return null;
  return calculateCasePlan(sourceQuantity, caseSize);
}

export function isValidLabelCount(count: number | null): count is number {
  return count !== null && Number.isSafeInteger(count) && count > 0;
}

export const MULTI_PACKAGE_LIMITS = {
  children: 2000,
  labels: 1000000,
  estimatedZipBytes: MAX_BROWSER_ZIP_BYTES,
} as const;

export type MultiPackageLimits = { children: number; labels: number; estimatedZipBytes: number };

export function effectiveMultiPackageLimits(limits?: Partial<MultiPackageLimits> | null): MultiPackageLimits {
  const validLimit = (value: unknown, fallback: number) => typeof value === 'number'
    && Number.isSafeInteger(value) && value >= 0 ? value : fallback;
  return {
    children: validLimit(limits?.children, MULTI_PACKAGE_LIMITS.children),
    labels: validLimit(limits?.labels, MULTI_PACKAGE_LIMITS.labels),
    estimatedZipBytes: browserZipLimit(limits?.estimatedZipBytes),
  };
}

export function estimateMultiPackageZipBytes(children: SelectedChildPackage[]): number {
  return children.reduce((total, child) => total + (isValidLabelCount(child.count) ? child.count * 16 * 1024 : 0) + 64 * 1024, 0);
}

export function selectionLimitErrors(children: SelectedChildPackage[], estimatedZipBytes: number = estimateMultiPackageZipBytes(children), configuredLimits: MultiPackageLimits = MULTI_PACKAGE_LIMITS): string[] {
  const limits = effectiveMultiPackageLimits(configuredLimits);
  const errors: string[] = [];
  if (children.length > limits.children) {
    errors.push(`Selected packages exceed the ${limits.children} package limit by ${children.length - limits.children}.`);
  }
  const labelTotal = children.reduce((sum, child) => sum + (isValidLabelCount(child.count) ? child.count : 0), 0);
  if (labelTotal > limits.labels) {
    errors.push(`Requested labels exceed the ${limits.labels} label limit by ${labelTotal - limits.labels}.`);
  }
  if (estimatedZipBytes !== null && estimatedZipBytes > limits.estimatedZipBytes) {
    errors.push(`Estimated ZIP exceeds the ${limits.estimatedZipBytes === 1024 ** 3 ? '1 GB' : `${limits.estimatedZipBytes} byte`} limit by ${estimatedZipBytes - limits.estimatedZipBytes} bytes.`);
  }
  return errors;
}

export function retryableResultItems(items: MultiPackagePrintResultItem[]): MultiPackagePrintResultItem[] {
  return items.filter((item) => item.status === 'Failed' || item.status === 'Unstarted');
}

export function resumableResultItems(items: MultiPackagePrintResultItem[]): MultiPackagePrintResultItem[] {
  return items.filter((item) => item.status === 'RecoveryRequired' && !isAwaitingMultiPackageOutput(item));
}

export type MultiPackageOutputNameParts = {
  parentPackageLabel: string;
  date: string;
  childPackageLabel: string;
  itemName: string;
};
